import { FDRStatus, OrgUserSearchParams, UserId } from '@cibo/core'
import {
  AuthUserPermission,
  Can,
  Loading,
  PaginatedUsersTable,
  USER_DATA_KEYS,
  useDataGridPersistence,
  useFDRAcresColumn,
  useFDRFieldCountColumn,
  useOrgUserColumns,
  useOrganizationList,
  usePermissions,
  useWindowTitle,
} from '@cibo/profile'
import { ActionBar } from '@cibo/ui'
import PersonAddRounded from '@mui/icons-material/PersonAddRounded'
import { LoadingButton } from '@mui/lab'
import { Button, Stack } from '@mui/material'
import { GridSortModel } from '@mui/x-data-grid-pro'
import { LayoutSection } from 'components/ImpactLayout'
import { ADMIN_ROUTES } from 'navigation/types'
import { Forbidden } from 'pages/Forbidden'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, generatePath, useNavigate, useSearchParams } from 'react-router-dom'

interface GridOptionsQueryString {
  page?: number
  search?: OrgUserSearchParams
  sortModel?: GridSortModel
}

export const UserList = () => {
  const { t } = useTranslation('impact/UserList')
  useWindowTitle(t('pageTitle'))
  const { can } = usePermissions()
  const navigate = useNavigate()
  const organizations = useOrganizationList()
  const [searchParams, setSearchParams] = useSearchParams()

  const availableColumns = useOrgUserColumns({
    // onClickOrganization: ({ organizationId }) =>
    //   navigate(
    //     generatePath(ADMIN_ROUTES.ORGANIZATION_DETAIL, {
    //       organizationId,
    //     })
    //   ),
  })
  const enrolledAcresColumn = useFDRAcresColumn(FDRStatus.enrolled, { sortable: false })
  const enrolledFieldCountColumn = useFDRFieldCountColumn(FDRStatus.enrolled, { sortable: false })
  const columns = useMemo(
    () =>
      !!organizations.data?.length
        ? [
            availableColumns.name,
            availableColumns.email,
            availableColumns.organization,
            enrolledAcresColumn,
            enrolledFieldCountColumn,
            availableColumns.totalFields,
            availableColumns.lastLoginDate,
          ]
        : [
            availableColumns.name,
            availableColumns.email,
            enrolledAcresColumn,
            enrolledFieldCountColumn,
            availableColumns.totalFields,
            availableColumns.lastLoginDate,
          ],
    [availableColumns, organizations.isFetched]
  )

  const persistenceProps = useDataGridPersistence({
    columns,
    key: USER_DATA_KEYS.USERS_TABLE_GLOBAL,
    options: ['columnOrder', 'columnVisibility', 'pageSize', 'columnWidth'],
  })

  const table = useMemo(() => {
    try {
      if (typeof searchParams.get('table') === 'string') {
        //@todo more sanity checks on what is parsed
        return JSON.parse(searchParams.get('table') as string) as GridOptionsQueryString
      }
    } catch (e) {
      console.log({ e })
    }
    return {} as GridOptionsQueryString
  }, [searchParams.get('table')])

  const updateSearchParams = (values: Record<string, any>) => {
    const paramsValue = JSON.stringify(values)
    setSearchParams({ table: paramsValue }, { replace: true })
  }

  if (!can({ useAny: AuthUserPermission.USER_DASHBOARD })) {
    return <Forbidden returnTo={'/'} />
  }

  if (!organizations.isFetched) {
    return <Loading />
  }

  return (
    <>
      <LayoutSection sx={{ flex: 1 }}>
        <PaginatedUsersTable
          {...persistenceProps}
          goToUser={({ userId }: { userId: UserId }) =>
            navigate(generatePath(ADMIN_ROUTES.USER_PROGRAMS, { userId: userId.toString() }))
          }
          baseQuery={{
            fdrStats: [{ status: FDRStatus.enrolled }, { status: FDRStatus.isEngaged }],
          }}
          onChangeSearchValues={search =>
            updateSearchParams({
              ...table,
              search,
            })
          }
          searchValues={table.search}
          page={table.page}
          headerPaletteColor="primary"
          actions={
            can({ useAny: AuthUserPermission.USER_CREATE }) ? (
              <Button
                color="secondary"
                variant="outlined"
                startIcon={<PersonAddRounded />}
                component={Link}
                to={ADMIN_ROUTES.USER_CREATE}
                data-testid="add-user-button"
              >
                {t('addUser')}
              </Button>
            ) : null
          }
          onPageChange={page =>
            updateSearchParams({
              ...table,
              page,
            })
          }
          sortModel={table.sortModel}
          onSortModelChange={sortModel => {
            updateSearchParams({
              ...table,
              sortModel,
            })
          }}
        />
      </LayoutSection>

      <Can useAny={[AuthUserPermission.USER_ARCHIVE, AuthUserPermission.EDIT_USER_ORGANIZATION]}>
        <ActionBar>
          <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={1}>
            <LoadingButton
              variant="outlined"
              onClick={console.log}
              disabled={!can({ useAny: AuthUserPermission.USER_ARCHIVE })}
            >
              {t('archiveUsers', { count: 1 })}
            </LoadingButton>
            <LoadingButton
              variant="contained"
              onClick={console.log}
              disabled={!can({ useAny: AuthUserPermission.EDIT_USER_ORGANIZATION })}
            >
              {t('moveUsers', { count: 1 })}
            </LoadingButton>
          </Stack>
        </ActionBar>
      </Can>
    </>
  )
}
